import type { GoogleCredential } from '../../types/google-login-button.types';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { environment as env } from '@env/environment';

@Component({
  selector: 'app-google-login-button',
  standalone: true,
  imports: [TranslateModule, HlmButtonImports],
  templateUrl: './google-login-button.component.html',
  styleUrl: "./google-login-button.component.scss",
})
export class GoogleLoginButtonComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('googleContainer', { static: true }) private readonly googleContainer!: ElementRef<HTMLDivElement>;

  @Input() clientId: string = env.googleClientId;
  @Input() uxMode: 'popup' | 'redirect' = 'popup';
  @Input() autoWidthSelector?: string;

  @Input() theme: 'outline' | 'filled_blue' | 'filled_black' = 'outline';
  @Input() size: 'large' | 'medium' | 'small' = 'large';
  @Input() text: 'continue_with' | 'signin_with' | 'signup_with' = 'continue_with';
  @Input() shape: 'pill' | 'rectangular' = 'pill';
  @Input() logoAlignment: 'left' | 'center' = 'left';

  @Output() credential = new EventEmitter<string>();
  @Output() error = new EventEmitter<string>();

  private readonly isBrowser: boolean;
  private resizeObs?: ResizeObserver;
  private bootTimer?: ReturnType<typeof setTimeout>;

  private onVisibility = () => {
    if (document.visibilityState === 'visible') this.rerender();
  };
  private onPageShow = (e: PageTransitionEvent) => {
    if ((e as any).persisted) this.rerender();
  };

  constructor(
    @Inject(PLATFORM_ID) platformId: Object,
    private readonly zone: NgZone,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngOnInit(): void {
    if (!this.isBrowser) return;

    (window as any).onGoogleCredential = (resp: GoogleCredential) => {
      this.zone.run(() => {
        const token = resp?.credential;
        token ? this.credential.emit(token) : this.error.emit('google_credential_missing');
      });
    };
  }

  ngAfterViewInit(): void {
    if (!this.isBrowser) return;

    this.tryRender();

    const target =
      (this.autoWidthSelector
        ? (document.querySelector(this.autoWidthSelector) as HTMLElement | null)
        : (this.googleContainer.nativeElement.parentElement as HTMLElement | null)) ?? null;

    if ('ResizeObserver' in window && target) {
      this.resizeObs = new ResizeObserver(() => this.rerender());
      this.resizeObs.observe(target);
    }

    document.addEventListener('visibilitychange', this.onVisibility, { passive: true });
    window.addEventListener('pageshow', this.onPageShow as any, { passive: true });
  }

  ngOnDestroy(): void {
    this.resizeObs?.disconnect();
    if (this.bootTimer) clearTimeout(this.bootTimer);
    if (this.isBrowser) {
      document.removeEventListener('visibilitychange', this.onVisibility);
      window.removeEventListener('pageshow', this.onPageShow as any);
    }
  }

  triggerGoogle(): void {
    if (!this.isBrowser) return;

    const idApi = (window as any).google?.accounts?.id;
    const host = this.googleContainer.nativeElement;

    if (!idApi) {
      this.tryRender(true);
      requestAnimationFrame(() => this.clickRenderedButton());
      return;
    }

    const exists = host.querySelector('iframe, div[role="button"]');
    if (!exists) {
      this.rerender();
      requestAnimationFrame(() => this.clickRenderedButton());
      return;
    }

    this.clickRenderedButton();
  }

  private clickRenderedButton(): void {
    const btn = this.googleContainer.nativeElement.querySelector('div[role="button"]') as HTMLElement | null;
    if (btn) btn.click();
    else this.error.emit('google_button_not_initialized');
  }

  private tryRender(force = false): void {
    const idApi = (window as any).google?.accounts?.id;
    if (!idApi) {
      this.bootTimer = setTimeout(() => this.tryRender(force), 200);
      return;
    }
    this.render(force);
  }

  private rerender(): void {
    const host = this.googleContainer.nativeElement;
    host.innerHTML = '';
    this.render(true);
  }

  private render(force = false): void {
    const idApi = (window as any).google?.accounts?.id;
    const host = this.googleContainer.nativeElement;
    if (!idApi || !host) return;
    if (!force && host.querySelector('iframe')) return;

    // Google Identity Services has one initialization per page. Resizing or
    // restoring this view should only re-render its button, not reinitialize it.
    const windowWithGoogleInit = window as Window & {
      __vaptGoogleIdentityApi?: unknown;
    };
    if (windowWithGoogleInit.__vaptGoogleIdentityApi !== idApi) {
      idApi.initialize({
        client_id: this.clientId,
        callback: (window as any).onGoogleCredential,
        ux_mode: this.uxMode,
        auto_select: false,
        use_fedcm_for_prompt: false,
      });
      windowWithGoogleInit.__vaptGoogleIdentityApi = idApi;
    }

    const width = this.computeWidth();

    host.innerHTML = '';
    const opts: any = {
      theme: this.theme,
      size: this.size,
      text: this.text,
      shape: this.shape,
      logo_alignment: this.logoAlignment,

    };
    if (width > 0) opts.width = width;

    idApi.renderButton(host, opts);
  }

  private computeWidth(): number {
    const target = this.autoWidthSelector
      ? (document.querySelector(this.autoWidthSelector) as HTMLElement | null)
      : (this.googleContainer.nativeElement.parentElement as HTMLElement | null);

    const w = target?.getBoundingClientRect().width ?? 0;
    const innerPadding = 32;
    return w > 0 ? Math.floor(w - innerPadding) : 0;
  }
}
